import { useState } from "react";
import { Loader2, Lock, Truck } from "lucide-react";
import { toast } from "sonner";
import { capi, apiError, money } from "@/lib/api";
import { inputCls } from "@/components/account/AuthCard";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export function CustomPurchase({ request }) {
  const [f, setF] = useState({ phone: "", full_name: request.customer?.name || "", line1: "", line2: "", city: "", state: "", postal_code: "" });
  const [rates, setRates] = useState([]);
  const [checkoutId, setCheckoutId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [rating, setRating] = useState(false);
  const [busy, setBusy] = useState(false);
  const base = `/account/requests/${request.request_number}`;

  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (rates.length) { setRates([]); setCheckoutId(null); setSelected(null); } };
  const ready = f.full_name && f.line1 && f.city && f.state && f.postal_code;

  const getRates = async () => {
    setRating(true);
    try {
      const { phone, ...addr } = f;
      const { data } = await capi.post(`${base}/shipping-rates`, { phone, shipping_address: { ...addr, country: "US" } });
      setRates(data.rates); setCheckoutId(data.checkout_id); setSelected(data.rates[0]?.rate_id || null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRating(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await capi.post(`${base}/checkout`, { origin_url: window.location.origin, shipping_checkout_id: checkoutId, shipping_rate_id: selected });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const ship = Number(rates.find((r) => r.rate_id === selected)?.amount || 0);

  return (
    <div data-testid="custom-purchase" className="rounded-lg border-2 border-kys-accent bg-white p-6">
      <p className="label-accent text-kys-accent">Approved — Ready to Purchase</p>
      <h2 className="mt-1 font-serif text-3xl text-kys-ink">Complete your purchase</h2>
      <p className="mt-1 text-sm text-kys-muted">Your design and price are locked in. Enter a U.S. shipping address to see live shipping rates.</p>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <input data-testid="cp-name" placeholder="Full name" value={f.full_name} onChange={set("full_name")} className={`${inputCls} sm:col-span-2`} />
        <input data-testid="cp-line1" placeholder="Address line 1" value={f.line1} onChange={set("line1")} className={`${inputCls} sm:col-span-2`} />
        <input data-testid="cp-line2" placeholder="Apt, suite (optional)" value={f.line2} onChange={set("line2")} className={`${inputCls} sm:col-span-2`} />
        <input data-testid="cp-city" placeholder="City" value={f.city} onChange={set("city")} className={inputCls} />
        <select data-testid="cp-state" value={f.state} onChange={set("state")} className={inputCls}>
          <option value="">State</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid="cp-zip" placeholder="ZIP code" value={f.postal_code} onChange={set("postal_code")} className={inputCls} />
        <input data-testid="cp-phone" placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={inputCls} />
      </div>

      <div className="mt-5">
        <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-kys-accent" /><p className="text-sm font-semibold text-kys-ink">Shipping method</p></div>
        {rates.length === 0 ? (
          <button data-testid="cp-get-rates" onClick={getRates} disabled={!ready || rating}
            className="mt-3 flex items-center gap-2 rounded-full bg-kys-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-kys-accent disabled:opacity-50">
            {rating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculate Shipping"}
          </button>
        ) : (
          <div className="mt-3 space-y-2" data-testid="cp-rate-options">
            {rates.map((r) => (
              <label key={r.rate_id} className={`flex cursor-pointer items-center justify-between gap-3 rounded border p-3 text-sm transition-colors ${selected === r.rate_id ? "border-kys-accent bg-kys-blush" : "border-kys-blush-border"}`}>
                <span className="flex items-center gap-3">
                  <input type="radio" checked={selected === r.rate_id} onChange={() => setSelected(r.rate_id)} className="accent-kys-accent" />
                  <span>{r.provider} {r.servicelevel}{r.tag && <span className="ml-2 text-[10px] font-semibold uppercase text-kys-accent">{r.tag}</span>}
                    {r.estimated_days != null && <span className="block text-xs text-kys-muted">Est. {r.estimated_days} day{r.estimated_days === 1 ? "" : "s"}</span>}</span>
                </span>
                <span className="font-mono">{money(Number(r.amount))}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 space-y-1 border-t border-kys-blush-border pt-4 text-sm">
        <div className="flex justify-between"><span>Custom order</span><span className="font-mono" data-testid="cp-price">{money(request.final_price)}</span></div>
        <div className="flex justify-between"><span>Shipping</span><span className="font-mono">{ship ? money(ship) : "—"}</span></div>
        <div className="flex justify-between text-kys-muted"><span>Sales tax</span><span>Calculated at payment</span></div>
      </div>
      <button data-testid="cp-pay-btn" onClick={pay} disabled={busy || !selected}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-kys-ink py-4 text-sm font-semibold text-white transition-colors hover:bg-kys-accent disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> {selected ? "Continue to Secure Payment" : "Select a shipping method"}</>}
      </button>
    </div>
  );
}
